import { getDroneCouponDiscount } from "../../../../../api/services/drone";
import {
  Text,
  Icon,
  Chip,
  Dialog,
  Button,
  HelperText,
} from "@virkesborsen/ui-component-library";
import { useState } from "react";
import { useDrone } from "../../../context/DroneContext";

const Card = ({ isSelected, onClick }) => {
  const textPrimaryColor = isSelected ? "text-white-50" : "text-charcoal-500";
  const textSecondaryColor = isSelected
    ? "text-purple-plum-100"
    : "text-green-spruce-800";
  const chipClasses = isSelected
    ? "bg-green-spruce-500 text-charcoal-500 fill-charcoal-500"
    : "bg-green-spruce-500/8 text-green-spruce-500 fill-green-spruce-500";
  const bgColor = isSelected ? "bg-charcoal-500" : "bg-white-50";

  const IconColor = isSelected
    ? "fill-spring-yellow-500 text-spring-yellow-500"
    : "fill-purple-plum-500 text-purple-plum-500";

  const hectareTextColor = isSelected ? "text-white-600" : "text-pebble-500";

  return (
    <div
      className={`flex flex-row gap-2 w-full border border-transparent justify-between p-4 rounded-xl cursor-pointer hover:border-pebble-500 ${bgColor}`}
      onClick={onClick}
    >
      <div className="flex flex-col">
        <div className="flex flex-row gap-1 items-baseline pb-1">
          <Text variant="labelLargeMedium" className={textPrimaryColor}>
            Skogsbruksplan
          </Text>
          <Text variant="labelMediumMedium" className={textSecondaryColor}>
            Premium
          </Text>
        </div>
        <Text
          variant="labelMediumRegular"
          className={`pb-3 ${textPrimaryColor}`}
        >
          Skogsbruksplan gjord med drönarflygning
        </Text>

        <Text variant="bodySmallRegular" className={`pb-4 ${textPrimaryColor}`}>
          Till skillnad från en helt digital skogsbruksplan görs denna utifrån
          de högupplösta ortofotona som drönaren tar. En sådan skogsbruksplan
          kan ge ett bättre underlag till exempelvis kolkrediter och andra
          åtgärder i skogen
        </Text>
        <div
          className={`flex flex-row gap-2 items-center ${
            isSelected ? "opacity-100" : "opacity-0"
          }`}
        >
          <Icon
            tw
            name={isSelected ? "CheckFilled" : "SparkleSimple"}
            size="large"
            className={
              isSelected ? "fill-spring-yellow-500" : "fill-purple-plum-500"
            }
          />
          <Text variant="labelSmallMedium" className={IconColor}>
            {isSelected ? "Tillagd" : ""}
          </Text>
        </div>
      </div>

      <div className="flex flex-col h-full justify-between items-end">
        <Chip
          label="Tillval"
          size="medium"
          icon="StarSimple"
          className={chipClasses}
        />

        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-row gap-1 items-baseline">
            <Text
              variant="displayAndHeadline2XtraSmallMedium"
              className={textPrimaryColor}
            >
              {99}
            </Text>
            <Text variant="labelLargeRegular" className={textPrimaryColor}>
              SEK
            </Text>
          </div>
          <Text variant="captionRegular" className={hectareTextColor}>
            per hektar
          </Text>
        </div>
      </div>
    </div>
  );
};

export default function ForestManagementPlan() {
  const {
    selectedProperties,
    selectedPackage,
    dronePrices,
    selectedForestManagementPlan,
    setSelectedForestManagementPlan,
    couponDiscount,
    setCouponDiscount,
  } = useDrone();
  const [isForestPlanPopupOpen, setIsForestPlanPopupOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const getDiscountLabel = (data) => {
    if (!data) return;

    if (data.coupon_discount_percentage < 5) {
      return `${data.coupon_discount_price} SEK rabatt`;
    }

    return `${data.coupon_discount_percentage}% rabatt`;
  };

  const handleForestPlanSelect = () => {
    if (selectedForestManagementPlan) {
      setSelectedForestManagementPlan(null);
    } else {
      setSelectedForestManagementPlan({
        id: 0,
        price: 99,
        internalName: "Skogsbruksplan Premium",
        minimumOrderAmount: 4170+2970,
      });
    }
  };

  const handleCouponCodeChange = async (e) => {
    const value = e.target.value;
    if (!value || value.length <= 3) return;

    const { data, error, message, ok } = await getDroneCouponDiscount({
      refs: selectedProperties.map((prop) => prop.ref),
      package_id: selectedPackage?.id,
      forest_management_plan_id: selectedForestManagementPlan?.id,
      coupon_code: value,
    });

    if (error) {
      setErrorMessage("Ogiltig rabattkod");
    }

    if (ok) {
      setCouponDiscount({
        coupon_code: value.toUpperCase(),
        label: getDiscountLabel(data),
        ...data,
      });
      setErrorMessage(null);
    }
  };

  const handleCouponCodeRemove = () => {
    setCouponDiscount(null);
  };

  if (!dronePrices?.packages) {
    return <div className="flex p-4">Laddar...</div>;
  }

  return (
    <div className="flex flex-col w-full">
      <div className="w-full flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          <Text variant="labelLargeMedium" className="pl-4">
            Välj tillval
          </Text>
          <Card
            isSelected={Boolean(selectedForestManagementPlan)}
            onClick={handleForestPlanSelect}
          />
          {/* <div
            className="flex flex-row gap-2 items-center cursor-pointer justify-between border border-white-600 rounded-xl p-4 hover:border-pebble-500"
            onClick={() => setIsForestPlanPopupOpen(true)}
          >
            <div>
              <Text variant="labelMediumMedium">Läs mer om skogsbruksplan</Text>
              <Text variant="labelMediumRegular" className="text-pebble-500">
                Upptäck din skog, få en skräddarsydd överblick.
              </Text>
            </div>
            <Icon tw name="PlusOutlined" size="large" />
          </div> */}
        </div>
      </div>

      <div className="w-full h-[0.5px] border border-stone-500/17 my-8" />

      <div className="w-full flex flex-col gap-2">
        <div className="w-full flex flex-col gap-4">
          <Text variant="labelLargeMedium" className="pl-4">
            Lägg till rabattkod
          </Text>
          <div className="flex flex-row gap-2 justify-between items-center bg-white-50 rounded-xl p-4 h-14">
            {couponDiscount ? (
              <>
                <div
                  className="bg-cloud-300 flex w-auto items-center justify-center gap-1 rounded-xs px-2 py-1 cursor-pointer hover:bg-cloud-500"
                  onClick={handleCouponCodeRemove}
                >
                  <Text variant="labelSmallMedium">
                    {couponDiscount.coupon_code}
                  </Text>
                  <Icon name="CrossSimple" size="medium" />
                </div>
                <Text variant="bodySmallRegular">{couponDiscount.label}</Text>
              </>
            ) : (
              <input
                type="text"
                placeholder=""
                className="border border-transparent bg-transparent w-full focus:outline-none"
                onChange={handleCouponCodeChange}
              />
            )}
          </div>
        </div>

        {errorMessage && (
          <HelperText
            icon="ExclamationFilled"
            label={errorMessage}
            className={"text-danger-500 fill-danger-500 pl-4"}
          >
            {errorMessage || " "}
          </HelperText>
        )}
      </div>

      {/* Forest Management Plan info popup */}
      <Dialog isOpen={isForestPlanPopupOpen} size="medium">
        <Dialog.Header>
          <div className="flex w-full justify-between items-start">
            <div className="flex flex-row gap-1 items-center justify-center">
              <Text variant="labelLargeMedium">Skogsbruksplan</Text> -
              <Text variant="labelMediumRegular" className="text-pebble-500">
                Se vad som ingår
              </Text>
            </div>
            <Button
              tw
              variant="secondary"
              size="icon"
              onClick={() => setIsForestPlanPopupOpen(false)}
            >
              <Icon
                tw
                name="CrossSimple"
                className="color-charcoal-500"
                size="medium"
              />
            </Button>
          </div>
        </Dialog.Header>
        <Dialog.Content className="flex flex-col gap-4">
          <Text variant="bodySmallRegular">
            En skogsbruksplan är ett värdefullt verktyg som hjälper dig att
            förstå och planera hur din skogsfastighet bäst kan användas och
            skötas. Den innehåller detaljerad information om fastigheten,
            inklusive trädslag, virkesvolym, ålder och rekommenderade åtgärder
            för skötsel och föryngring.
          </Text>
          <div className="flex flex-col gap-1">
            <Text variant="labelLargeMedium">Anpassa och planera enkelt</Text>
            <Text variant="bodySmallRegular">
              Rita om gränser, markera områden och lägg till kommentarer direkt
              i kartvyn. Anpassa planen efter dina behov och skapa en tydlig
              handlingsplan.
            </Text>
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="labelLargeMedium">Få koll på ekonomin</Text>
            <Text variant="bodySmallRegular">
              Se värdet på din fastighet, förväntade intäkter, kostnader och
              klimatpåverkan. Allt samlat på ett och samma ställe, så att du kan
              ta vara på din skogs fulla potential.
            </Text>
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="labelLargeMedium">Upptäck skogens potential</Text>
            <Text variant="bodySmallRegular">
              Utforska kartlager och analysverktyg som visar skogens tillstånd,
              volymer och fördelningar. Riskanalyser för granbarkborre och brand
              ger dig insikter för att skydda din skog.
            </Text>
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="labelLargeMedium">Ta rätt beslut, snabbt</Text>
            <Text variant="bodySmallRegular">
              All data är alltid uppdaterad, vilket gör det enkelt att agera i
              rätt tid. Få tillgång till rådgivning och verktyg för gallring och
              avverkning eller klimat anpassning, såsom möjligheten att sälja
              kolkrediter.
            </Text>
          </div>
        </Dialog.Content>
      </Dialog>
    </div>
  );
}
